import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useApiFetch } from '../api';
import type { Pagination } from '../types/models/Invoice';
import type { WalletTransaction } from '../types/models/Wallet';
import type { ApiResponseWalletTransactions } from '../types/api/ApiResponseWallet';

interface UseWalletTransactionsParams {
  page: number;
  pageSize: number;
  enabled?: boolean;
}

interface UseWalletTransactionsReturn {
  transactions: WalletTransaction[];
  /** `null` until the first successful response. */
  pagination: Pagination | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * `GET /clients/wallet/transactions?page&pageSize` — the tenant's statement, newest first, paginated
 * server-side (docs/api-types/wallet.md). Mirrors `useInvoices`: exactly one page is fetched, and a
 * superseded request is aborted so a stale response can never overwrite a newer one.
 */
export const useWalletTransactions = ({
  page,
  pageSize,
  enabled = true,
}: UseWalletTransactionsParams): UseWalletTransactionsReturn => {
  const [transactions, setTransactions] = useState<WalletTransaction[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [inFlight, setInFlight] = useState(false);
  const [settledKey, setSettledKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const auth = useAuth();
  const apiFetch = useApiFetch();
  const abortRef = useRef<AbortController | null>(null);

  const paramsKey = new URLSearchParams({ page: String(page), pageSize: String(pageSize) }).toString();
  // Derived, not a plain flag: true on the very render that carries new parameters (see useInvoices).
  const loading = enabled && (inFlight || settledKey !== paramsKey);

  const fetchTransactions = useCallback(async () => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setInFlight(true);
    setError(null);

    try {
      const response = await apiFetch(`/clients/wallet/transactions?${paramsKey}`, { signal: controller.signal });
      // JSend: branch on body.status, never response.ok.
      const body = (await response.json().catch(() => null)) as ApiResponseWalletTransactions | null;
      if (!body || body.status !== 'success' || !body.data?.transactions || !body.data.pagination) {
        throw new Error(body?.message ?? 'Could not load your statement.');
      }
      setTransactions(body.data.transactions);
      setPagination(body.data.pagination);
    } catch (err) {
      if ((err instanceof DOMException && err.name === 'AbortError') || controller.signal.aborted) {
        return;
      }
      setError(err instanceof Error ? err.message : 'An error occurred while loading your statement.');
    } finally {
      if (abortRef.current === controller) {
        setInFlight(false);
        setSettledKey(paramsKey);
      }
    }
  }, [apiFetch, paramsKey]);

  useEffect(() => {
    if (enabled && auth.isAuthenticated) {
      void fetchTransactions();
    }
  }, [enabled, auth.isAuthenticated, fetchTransactions]);

  return { transactions, pagination, loading, error, refetch: fetchTransactions };
};

export default useWalletTransactions;
